

import { useState, useCallback, useRef, useEffect, createContext, useContext } from "react";
import { createPortal } from "react-dom";

export type ToastType = "success" | "error" | "info" | "warning";

/**
 * One button on the toast — "Undo" after a write that had a side effect the
 * operator should be able to take back on the spot. Clicking it runs
 * `onClick` and dismisses the toast.
 */
export type ToastAction = { label: string; onClick: () => void };

type Toast = {
  id: number;
  message: string;
  type: ToastType;
  action?: ToastAction;
  /** An action toast times itself (it pauses); a plain one is timed by the provider. */
  durationMs: number;
};

type ToastContextValue = {
  /** `durationMs` (default 3000): a toast that carries something the reader
   *  must ACT on — a URL to copy by hand, an action button — needs longer
   *  than a status word. */
  showToast: (message: string, type: ToastType, durationMs?: number, action?: ToastAction) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

const NOOP_TOAST: ToastContextValue = { showToast: () => {} };

/**
 * The toast where a provider MAY be missing — a component that is mounted
 * under the app's provider but also rendered by hosts (and tests) that never
 * wrap one. Without a provider the toast is a no-op, never a crash: the
 * feedback it would carry is secondary to the render it would otherwise take
 * down (the activity dialog, the undo pill).
 */
export function useToastOptional(): ToastContextValue {
  return useContext(ToastContext) ?? NOOP_TOAST;
}

const TYPE_STYLES: Record<ToastType, string> = {
  success: "surface-dark font-light-primary",
  error: "surface-error-dark font-light-everytime",
  info: "surface-dark font-light-primary",
  // Something the operator should know happened, not a failure: the same
  // tokens as the in-dialog warning boxes, announced politely (role=status).
  warning: "chip-yellow-bg hint-orange-outline border font-primary",
};

const TOAST_BOX = "px-4 py-2 rounded-2xl shadow-2xl body-2-medium max-w-[calc(100vw-2rem)] break-words pointer-events-auto animate-[fadeIn_0.2s_ease] motion-reduce:animate-none";

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType, durationMs = 3000, action?: ToastAction) => {
    const id = nextId.current++;
    setToasts((prev) => {
      const next = [...prev, { id, message, type, durationMs, ...(action ? { action } : {}) }];
      return next.length > 3 ? next.slice(-3) : next;
    });
    // A toast with an action runs its own, pausable clock (`ActionToast`).
    if (!action) setTimeout(() => dismiss(id), durationMs);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // The live region stays PERMANENTLY mounted (never gate on toasts.length):
  // screen readers only announce content ADDED to an existing live region —
  // a region that appears together with its first message is silent.
  if (!mounted) return null;

  return createPortal(
    <div
      aria-live="polite"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[10002] flex flex-col gap-2 items-center pointer-events-none"
    >
      {toasts.map((toast) => (toast.action
        ? <ActionToast key={toast.id} toast={toast} action={toast.action} onDismiss={onDismiss} />
        : (
          <div
            key={toast.id}
            role={toast.type === "error" ? "alert" : "status"}
            data-testid="app-toast"
            data-toast-type={toast.type}
            className={`${TYPE_STYLES[toast.type]} ${TOAST_BOX}`}
          >
            {toast.message}
          </div>
        )))}
    </div>,
    document.body,
  );
}

/**
 * A toast with a button (audit r2 L2-F7, WCAG 2.2.1). Its clock stops while
 * the pointer is over it or the focus is inside it — reaching "Undo" by
 * keyboard from the far end of the page takes longer than its 8 s — and runs
 * the time that was left once both are gone. The button hands the focus back
 * to where it was before the toast took it, rather than dropping it on <body>
 * when the toast unmounts.
 */
function ActionToast({ toast, action, onDismiss }: {
  toast: Toast; action: ToastAction; onDismiss: (id: number) => void;
}) {
  const remaining = useRef(toast.durationMs);
  const startedAt = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hovered = useRef(false);
  const focused = useRef(false);
  const returnFocusTo = useRef<HTMLElement | null>(null);

  const run = useCallback(() => {
    if (timer.current !== null || hovered.current || focused.current) return;
    startedAt.current = Date.now();
    timer.current = setTimeout(() => onDismiss(toast.id), Math.max(0, remaining.current));
  }, [onDismiss, toast.id]);
  const pause = useCallback(() => {
    if (timer.current === null) return;
    clearTimeout(timer.current);
    timer.current = null;
    remaining.current -= Date.now() - startedAt.current;
  }, []);

  useEffect(() => {
    run();
    // `pause`, not a bare clearTimeout: it also drops the handle. Under
    // StrictMode (dev, the e2e lane) the effect runs, cleans up and runs
    // again — a kept handle made the second `run` think a clock was running,
    // and the toast never closed (audit r3 L2-F1).
    return pause;
  }, [run, pause]);

  return (
    <div
      role={toast.type === "error" ? "alert" : "status"}
      data-testid="app-toast"
      data-toast-type={toast.type}
      className={`${TYPE_STYLES[toast.type]} ${TOAST_BOX} flex items-center gap-3`}
      onMouseEnter={() => { hovered.current = true; pause(); }}
      onMouseLeave={() => { hovered.current = false; run(); }}
      onFocus={(e) => {
        if (!focused.current) {
          const from = e.relatedTarget;
          returnFocusTo.current = from instanceof HTMLElement && !e.currentTarget.contains(from) ? from : null;
        }
        focused.current = true;
        pause();
      }}
      onBlur={(e) => {
        if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
        focused.current = false;
        run();
      }}
    >
      <span className="min-w-0">{toast.message}</span>
      <button
        type="button"
        data-testid="app-toast-action"
        className="shrink-0 body-2-bold underline underline-offset-2 focus-ring rounded"
        onClick={() => {
          const back = returnFocusTo.current;
          if (back?.isConnected) back.focus();
          onDismiss(toast.id);
          action.onClick();
        }}
      >
        {action.label}
      </button>
    </div>
  );
}
